import { DetailLocationParamsPath } from 'hooks/useDetailLocationParams';
import { CollectionLocationParamsPath } from 'hooks/useCollectionLocationParams';
import { Broadcast, Episode, Movie, NowAndNext, Recording, Series, ContinueWatchingItems, TVSeriesDetailEpisode } from 'types/Asset';
import { DetailRouteDomain } from 'types/RouteTypes';
import { EpgProgram } from 'types/EpgTypes';
import { PlayerProps, PlayingAssetType, VodPlayerAssetInfo } from 'types/Player';
import { RecordingPlayerInitiator } from 'types/RecordingTypes';
import { CastAssetType } from 'types/Cast';
import { isBroadcast, isEpisodeRecording, isRecording, isTVEpisodeBroadcast } from './fnTypeGuards';

const HUUB_AND_KIDS_PATH_TERMS = ['huub', 'kids', 'lastele', 'детям'];

export const isHuubOrKidsPath = (pathname: string): boolean => {
    const path = pathname?.toLowerCase() ?? '';
    return HUUB_AND_KIDS_PATH_TERMS.some((term) => path.includes(term));
};

export const getFilterValue = (filter: string[], key: string): string | null => {
    if (!filter?.length) return null;
    let found = null;

    filter.forEach((filterValue) => {
        const explodeRegExp = /(.*)\[(.*)\]/g;
        const matches = explodeRegExp.exec(filterValue);
        let name: string;
        let value: string;
        if (matches) {
            [, name, value] = matches;

            // In an ideal world this should be fixed in BE, not allowing such inputs/saving/whatever
            // But look around, do we live in an ideal world? Is that BE going to phased out anyway?
            if (name.replace('\t', '') === key) {
                found = value;
            }
        }
    });

    return found;
};

export const appendQueryParams = (url: string, queries: { [key: string]: boolean | number | string }): string => {
    let finalUrl = url;

    Object.keys(queries).forEach((queryKey) => {
        finalUrl += !finalUrl.includes('?') ? '?' : '&';
        finalUrl += `${queryKey}=${queries[queryKey]}`;
    });

    return finalUrl;
};

export const encodeId = (id: string): string => id?.replace(/\./g, '~dot');

export const decodeId = (id: string): string => id?.replace(/~dot/g, '.');

export const buildDetailPageUrl = (locationParamsPath: DetailLocationParamsPath, targetPath: string): string => {
    if (targetPath == null) {
        return null;
    }

    const deepnessUrlSeparator = targetPath.indexOf('?') > -1 ? '&' : '?';

    if (!locationParamsPath?.params || Object.keys(locationParamsPath?.params ?? {}).length === 0) {
        const parentPath = (locationParamsPath?.pathname ?? '/') === '/' ? '' : locationParamsPath.pathname;
        return `${parentPath}${targetPath}${deepnessUrlSeparator}`;
    }

    let detailToDetailPath = '';

    if (locationParamsPath?.params?.section) {
        detailToDetailPath += '/';
        detailToDetailPath += locationParamsPath.params.section === '/' ? '' : locationParamsPath.params.section;
    }

    if (locationParamsPath?.params?.subSection) {
        detailToDetailPath += '/';
        detailToDetailPath += locationParamsPath.params.subSection === '/' ? '' : locationParamsPath.params.subSection;
    }

    if (locationParamsPath?.params?.sectionId) {
        if (detailToDetailPath.length > 0) {
            detailToDetailPath += '/';
        }

        detailToDetailPath += locationParamsPath.params.sectionId;
    }

    if (targetPath) {
        detailToDetailPath += targetPath;
    }

    detailToDetailPath += `${deepnessUrlSeparator}`;

    return detailToDetailPath;
};

// OAuth callback params left in the URL by Keycloak after the login redirect (happend occasionally). This will strip them.
const OAUTH_CALLBACK_PARAMS = ['code', 'session_state', 'iss', 'state', 'error', 'error_description'];

export const stripOAuthCallbackParams = (search: string): URLSearchParams => {
    const params = new URLSearchParams(search);
    OAUTH_CALLBACK_PARAMS.forEach((param) => params.delete(param));
    return params;
};

export const buildCollectionPageUrl = (locationParamsPath: CollectionLocationParamsPath, targetPath: string): string => {
    if (targetPath == null) {
        return null;
    }

    if (!locationParamsPath.params || Object.keys(locationParamsPath?.params ?? {}).length === 0) {
        const parentPath = (locationParamsPath?.pathname ?? '/') === '/' ? '' : locationParamsPath.pathname;
        return `${parentPath}${targetPath}`;
    }

    let toCollectionPath = '';

    if (locationParamsPath?.params?.section) {
        toCollectionPath += '/';
        toCollectionPath += locationParamsPath.params.section === '/' ? '' : locationParamsPath.params.section;
    }

    if (locationParamsPath?.params?.sectionId) {
        if (toCollectionPath.length > 0) {
            toCollectionPath += '/';
        }

        toCollectionPath += locationParamsPath.params.sectionId;
    }

    if (targetPath) {
        toCollectionPath += targetPath;
    }

    return toCollectionPath;
};

// Cyrillic -> Latin transliteration so non-Latin titles (read: "Russian") still produce a readable, URL-safe slug instead of being empty.
const CYRILLIC_MAP: { [key: string]: string } = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    е: 'e',
    ё: 'e',
    ж: 'zh',
    з: 'z',
    и: 'i',
    й: 'i',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'ts',
    ч: 'ch',
    ш: 'sh',
    щ: 'shch',
    ъ: '',
    ы: 'y',
    ь: '',
    э: 'e',
    ю: 'yu',
    я: 'ya',
};

const transliterateCyrillic = (string: string): string => string.replace(/[Ѐ-ӿ]/g, (char) => CYRILLIC_MAP[char.toLowerCase()] ?? '');

// TODO: use to generate url safe live tv channel map
export const slugify = (string: string) => {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìıİłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return transliterateCyrillic(string.toString())
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
        .replace(/&/g, '-and-') // Replace & with 'and'
        .replace(/[^\w-]+/g, '') // Remove all non-word characters
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end of text
};

// Small hack: Detail-route segments like: "filmid" -> a collection url "slug" must not equal same, or the detail route overwrites it.
const RESERVED_DETAIL_SEGMENTS = new Set(
    Object.values(DetailRouteDomain).flatMap((routes) => Object.values(routes).map((segment) => segment.toLowerCase()))
);

// Title slug for a collection URL prefixed.
export const collectionSlug = (title: string): string => {
    const slug = slugify(title ?? '') || 'collection';
    return RESERVED_DETAIL_SEGMENTS.has(slug) ? `collection-${slug}` : slug;
};

/**
 * Detail page urls
 */

export const getBroadcastDetailsURL = (
    broadcast: Broadcast | EpgProgram | Recording | TVSeriesDetailEpisode,
    locationParamsPath: DetailLocationParamsPath,
    appLanguage: string
): string => {
    if (isRecording(broadcast)) {
        if (isEpisodeRecording(broadcast)) {
            const { programSeriesId, id } = broadcast;
            return buildDetailPageUrl(
                locationParamsPath,
                `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(programSeriesId)}?event=${id}`
            );
        }

        const { programSeriesId, id } = broadcast;

        if (programSeriesId) {
            return buildDetailPageUrl(
                locationParamsPath ?? {},
                `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(programSeriesId)}?event=${id}`
            );
        }

        return id ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].PROGRAM}/${encodeId(id)}`) : null;
    }

    if (isTVEpisodeBroadcast(broadcast) && (broadcast as any).seriesId) {
        if (isBroadcast(broadcast)) {
            const { seriesId, episodeId } = broadcast;
            return buildDetailPageUrl(
                locationParamsPath,
                `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(seriesId)}?episode=${episodeId}`
            );
        }

        const { seriesId, episodeId, id } = broadcast;
        return buildDetailPageUrl(
            locationParamsPath,
            `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(seriesId || episodeId)}?event=${id}`
        );
    }

    if ((broadcast as any).type === 'tv_series_episode') {
        const { id } = broadcast;
        const programSeriesId = (broadcast as any).program_series_id ?? (broadcast as any).programSeriesId;
        const eventId = (broadcast as any).listings?.[0]?.id ?? id;
        const path =
            programSeriesId && eventId
                ? buildDetailPageUrl(
                      locationParamsPath ?? {},
                      `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(programSeriesId)}?event=${eventId}`
                  )
                : id
                  ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(id)}`)
                  : null;
        return path;
    }

    const { id } = broadcast;
    const detailId = (broadcast as any).program_id || id;

    const finalPath = detailId
        ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].PROGRAM}/${encodeId(detailId)}`)
        : null;
    return finalPath;
};

export const getNowAndNextDetailsURL = (
    nowAndNext: NowAndNext,
    locationParamsPath: DetailLocationParamsPath,
    appLanguage: string
): string => {
    const { seriesId, episodeId } = nowAndNext;

    if (seriesId != null) {
        return buildDetailPageUrl(locationParamsPath, `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(seriesId)}`);
    }

    const targetId = episodeId ?? nowAndNext.id;
    return targetId
        ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].PROGRAM}/${encodeId(targetId)}`)
        : null;
};

export const getContinueWatchingURL = (
    continueWatching: ContinueWatchingItems,
    locationParamsPath: DetailLocationParamsPath,
    appLanguage: string
): string => {
    const { id, kind, program_series_id, originalType, vod_series_id } = continueWatching;

    if (!id) return null;

    let routeDomain = DetailRouteDomain[appLanguage].PROGRAM;
    const lowercaseKind = kind?.toLowerCase();
    let urlId = id;

    switch (lowercaseKind) {
        case 'movie':
            if (originalType === 'VOD_ASSET') {
                routeDomain = DetailRouteDomain[appLanguage].VOD_MOVIE;
            } else if (originalType === 'LISTING') {
                routeDomain = DetailRouteDomain[appLanguage].PROGRAM;
                urlId = id;
            } else {
                routeDomain = DetailRouteDomain[appLanguage].VOD_MOVIE;
            }
            break;

        case 'episode':
            if (program_series_id) {
                routeDomain = DetailRouteDomain[appLanguage].TV_SERIES;
                urlId = program_series_id;
            } else {
                routeDomain = DetailRouteDomain[appLanguage].VOD_SERIES;
                urlId = vod_series_id || id;
            }
            break;

        default:
            routeDomain = DetailRouteDomain[appLanguage].PROGRAM;
            break;
    }

    const baseUrl = `/${routeDomain}/${encodeId(urlId)}`;

    const result = buildDetailPageUrl(locationParamsPath ?? {}, baseUrl);
    return result;
};

export const getRecordingDetailURL = (recording: Recording, locationParamsPath: DetailLocationParamsPath, appLanguage: string): string => {
    const { id } = recording;
    return id ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].RECORDING}/${encodeId(id)}`) : null;
};

export const getVodMovieDetailsURL = (movie: Movie, locationParamsPath: DetailLocationParamsPath, appLanguage: string): string => {
    const { id } = movie;

    return id ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].VOD_MOVIE}/${encodeId(id)}`) : null;
};

export const getProgramsDetailsURL = (movie: Movie, locationParamsPath: DetailLocationParamsPath, appLanguage: string): string => {
    const { id } = movie;

    return id ? buildDetailPageUrl(locationParamsPath ?? {}, `/${DetailRouteDomain[appLanguage].PROGRAM}/${encodeId(id)}`) : null;
};

export const getSeriesDetailUrl = (series: Series, locationParamsPath: DetailLocationParamsPath, appLanguage: string): string => {
    const { id, isVOD, type } = series;
    const detailDomain =
        isVOD || type === 'vod_series' ? DetailRouteDomain[appLanguage].VOD_SERIES : DetailRouteDomain[appLanguage].TV_SERIES;

    return id ? buildDetailPageUrl(locationParamsPath ?? {}, `/${detailDomain}/${encodeId(id)}`) : null;
};

export const getSeriesDetailsUrl = (
    seriesId: string,
    isVod: boolean,
    locationParamsPath: DetailLocationParamsPath,
    appLanguage: string,
    channelId?: number
): string => {
    const detailDomain = isVod ? DetailRouteDomain[appLanguage].VOD_SERIES : DetailRouteDomain[appLanguage].TV_SERIES;

    const channelParam = channelId != null ? `?channelId=${channelId}` : '';
    return seriesId ? buildDetailPageUrl(locationParamsPath ?? {}, `/${detailDomain}/${encodeId(seriesId)}${channelParam}`) : null;
};

export const getTVSeriesEpisodeDetailUrl = (
    episode: TVSeriesDetailEpisode,
    locationParamsPath: DetailLocationParamsPath,
    appLanguage: string
): string => {
    const { id } = episode;

    return id ? buildDetailPageUrl(locationParamsPath, `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(id)}`) : null;
};

export const getVODSeriesEpisodeDetailUrl = (
    episode: Episode,
    locationParamsPath: DetailLocationParamsPath,
    appLanguage: string
): string => {
    const { episodeId, vod_series_id, id } = episode;
    const seriesId = vod_series_id || id;

    const episodeParam = vod_series_id ? `?episode=${episodeId}` : '';

    return seriesId
        ? buildDetailPageUrl(locationParamsPath, `/${DetailRouteDomain[appLanguage].VOD_SERIES}/${encodeId(seriesId)}${episodeParam}`)
        : null;
};

export const getCatchupPlayOptionDetailURL = (playOption: any, appLanguage: any): string => {
    const { programSeriesId, id } = playOption;

    return `/${DetailRouteDomain[appLanguage].TV_SERIES}/${encodeId(programSeriesId)}?event=${id}`;

    /* return `/${DetailRouteDomain[appLanguage].PROGRAM}/${encodeId(eventId)}`; Needs "entitlements" task to be completed */
};

export const getVODPlayOptionDetailURL = (playOption: VodPlayerAssetInfo, appLanguage: string): string => {
    const { id, seriesId } = playOption;

    if (seriesId) {
        return `/${DetailRouteDomain[appLanguage].VOD_SERIES}/${encodeId(seriesId)}?episode=${id}`;
    }

    return `/${DetailRouteDomain[appLanguage].VOD_MOVIE}/${encodeId(id)}`;
};

/**
 * Player page urls
 */

export const getLivePlayerURL = (
    channelId: string,
    options?: {
        mode?: 'normal' | 'time_shift';
        live?: boolean;
    }
): string => {
    const params = new URLSearchParams();

    if (options?.mode) {
        params.set('mode', options.mode);
    }

    if (options?.live) {
        params.set('live', 'true');
    }

    const queryString = params.toString();
    return `/play/live/${encodeId(channelId)}${queryString ? `?${queryString}` : ''}`;
};
export const getCatchupPlayerURL = (eventId: string): string => `/play/catchup/${encodeId(eventId)}`;
export const getVodPlayerURL = (assetId: string): string => `/play/vod/${encodeId(assetId)}`;
export const getTrailerPlayerURL = (id: string): string => `/play/trailer/${encodeId(id)}`;
export const getRecordingPlayerURL = (id: string, initiator: RecordingPlayerInitiator = 'recording'): string =>
    `/play/recording/${encodeId(id)}${initiator !== 'recording' ? `?initiator=${initiator}` : ''}`;

export const getContinueWatchingPlayerURL = (continueWatching: ContinueWatchingItems): string | null => {
    const { id, episode_id, originalType } = continueWatching;
    if (!id) return null;

    switch (originalType) {
        case 'VOD_ASSET':
            return getVodPlayerURL(id);
        case 'LISTING':
            return getCatchupPlayerURL(episode_id || id);
        default:
            return null;
    }
};

export const getPlayerURLForCastMiniController = (contentId: string, contentType: CastAssetType) => {
    if (!contentId || !contentType) {
        return null;
    }

    switch (contentType) {
        case 'LIVE':
            return getLivePlayerURL(contentId);
        case 'REPLAY':
            return getCatchupPlayerURL(contentId);
        case 'RECORDING':
            return getRecordingPlayerURL(contentId, 'broadcast');
        case 'VOD':
            return getVodPlayerURL(contentId);
        case 'TRAILER':
            return getTrailerPlayerURL(contentId);
        default:
            return null;
    }
};

export const getPlayerUrlForPlayerProps = (props: PlayerProps): string => {
    if (!props || !props.type) {
        return null;
    }

    switch (props.type) {
        case 'live' as PlayingAssetType:
        case PlayingAssetType.LIVE:
            return getLivePlayerURL(props.params.channelId);
        case PlayingAssetType.CATCHUP:
            return getCatchupPlayerURL(props.params.broadcastId);
        case PlayingAssetType.RECORDING:
            return getRecordingPlayerURL(props.params.recordingId, 'broadcast');
        case PlayingAssetType.VOD:
            return getVodPlayerURL(props.params.assetId);
        case PlayingAssetType.TRAILER:
            return getTrailerPlayerURL(props.params.assetId);
        default:
            return null;
    }
};
